import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PrimeNGConfig } from 'primeng/api';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { AuthorProfile, CategoryResponse, OutletProfile, Search } from 'app/models';
import {
  AuthenticationService,
  AuthorService,
  OutletService,
  SearchFilterDefaultsService,
} from 'app/services';
import { head, map } from 'lodash/fp';
import {
  AUTHOR_INFLUENCES, FOLLOWED_AUTHORS_FILTER, FOLLOWED_OUTLETS_FILTER,
  LOCATIONS, MATCH_TYPES,
  OUTLET_INFLUENCES,
  TIMEFRAMES,
} from 'app/constants';
import { OutletGroups, SelectOption } from '../../interfaces';
import { selectableOutletGroups } from 'app/services/outlet/outlet-group-options';
import { eq } from 'lodash';
import {
  resetAdvancedSearchFilters,
  resetAuthorProfile,
  resetOutletProfile,
  setOutletGroups,
  unSetInputSearchPlaceholder,
} from 'app/redux/actions';

export enum FilterType {
  ADVANCED_SEARCH = 'ADVANCED_SEARCH',
  NEWS_FEED = 'NEWS_FEED',
  COVERAGE_REPORT = 'COVERAGE_REPORT',
}

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
})
export class AdvancedSearchComponent implements OnInit, OnChanges {
  public showFilters: boolean;
  public timeframes: SelectOption[];
  public matchTypes: SelectOption[];
  public authorInfluences: SelectOption[];
  public outletInfluences: SelectOption[];
  public authorFollowed: SelectOption[];
  public outletFollowed: SelectOption[];
  public locations: SelectOption[];
  public outletGroup: SelectOption[];
  public timeframeSelected: string;
  public matchTypeSelected: string;
  public authorFollowSelected: string;
  public outletFollowSelected: string;
  public authorInfluSelected: boolean;
  public outletInfluSelected: boolean;
  public analystSelected: string;
  public locationSelected: string;
  public outletGroupSelected: number[];
  public fromDate: Date;
  public toDate: Date;
  public selectedAuthorsInclude: any[];
  public selectedAuthorsExclude: any[];
  public selectedOutletsInclude: any[];
  public selectedOutletsExclude: any[];
  public filteredAuthors: any[];
  public authors: any[];
  public filteredOutlets: any[];
  public outlets: any[];
  public searching: boolean;
  private authorSubjectKeyUp = new Subject<any>();
  private outletSubjectKeyUp = new Subject<any>();
  public currentPlaceholder: string;
  public authorProfile: AuthorProfile;
  public outletProfile: OutletProfile;
  public isLoadingGroups: boolean;
  public categories: CategoryResponse[];
  public authorLabels: number[];
  public excludeAuthorLabels: number[];
  public outletLabels: number[];
  public excludeOutletLabels: number[];
  private filtersSnapshot: any;
  private committingFilters: boolean;
  private ignoreDefaultsInitialized: boolean;

  @ViewChild('customTimeframe', { static: false }) private customTimeframe: any;
  @Input() reset: boolean;
  @Input() filterType: string = FilterType.ADVANCED_SEARCH;
  @Input() setSearchFromTypeahead: Search;
  @Input() visible: boolean;
  @Input() keywords: string;
  @Input() searchFilters: Search;
  @Input() embedded = false;
  @Output() toggle: EventEmitter<any> = new EventEmitter();
  @Output() advanceSearchOptions: EventEmitter<Search> = new EventEmitter();
  @Output() newsFeedFilters: EventEmitter<Search> = new EventEmitter();
  @Output() coverageFilters: EventEmitter<Search> = new EventEmitter();
  @Output() outletGroupOptionsChange: EventEmitter<SelectOption[]> = new EventEmitter();

  constructor(
    private authenticationService: AuthenticationService,
    private store: Store<AppState>,
    private primengConfig: PrimeNGConfig,
    private authorService: AuthorService,
    private outletService: OutletService,
    private searchFilterDefaults: SearchFilterDefaultsService
  ) {
    this.primengConfig.ripple = true;
    this.showFilters = false;
    this.timeframes = TIMEFRAMES;
    this.matchTypes = MATCH_TYPES;
    this.outletInfluences = OUTLET_INFLUENCES;
    this.authorInfluences = AUTHOR_INFLUENCES;
    this.authorFollowed = FOLLOWED_AUTHORS_FILTER;
    this.outletFollowed = FOLLOWED_OUTLETS_FILTER;
    this.locations = LOCATIONS;
    this.timeframeSelected = 'ONE_YEAR';
    this.matchTypeSelected = 'STANDARD';
    this.authorInfluSelected = false;
    this.outletInfluSelected = false;
    this.authorFollowSelected = null;
    this.outletFollowSelected = null;
    this.analystSelected = null;
    this.locationSelected = null;
    this.outletGroupSelected = [];
    this.searching = false;
    this.isLoadingGroups = false;
    this.categories = [];
    this.authorLabels = [];
    this.excludeAuthorLabels = [];
    this.outletLabels = [];
    this.excludeOutletLabels = [];
    this.ignoreDefaultsInitialized = false;
  }
  ngOnChanges(changes): void {
    if (changes.keywords) {
    }

    if (changes.visible) {
      this.showFilters = changes.visible.currentValue;
      if (changes.visible.currentValue) {
        this.snapshotFilters();
      }
    }

    if (changes.reset) {
      this.onResetFilter();
    }

    if (changes.filterType) {
      this.filterType = changes.filterType.currentValue;
    }

    if (changes.setSearchFromTypeahead?.currentValue) {
      const { authors, outlets } = changes.setSearchFromTypeahead.currentValue;
      if (authors.length > 0) {
        this.getAuthorById(head(authors));
      }

      if (outlets.length > 0) {
        this.getOutletById(head(outlets));
      }
    }

    if (changes.searchFilters) {
      this.hydrateSearchFilters(changes.searchFilters.currentValue);
    }
  }

  ngOnInit(): void {
    this.getOutletGroups();
    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody?.authors.length > 0) {
        searchBody.authors.forEach((id) => {
          this.getAuthorById(id);
        });
      }

      if (searchBody?.outlets.length > 0) {
        searchBody.outlets.forEach((id) => {
          this.getOutletById(id);
        });
      }
    });

    this.store.select('advancedSearch').subscribe(({ onResetFilters }) => {
      if (onResetFilters) {
        this.onResetFilter();
        this.handleAdvanceSearch();
      }
    });

    this.store
      .select('ui')
      .subscribe(({ inputSearchPlaceholderCurrentValue }) => {
        this.currentPlaceholder = inputSearchPlaceholderCurrentValue;
      });

    this.store
      .select('authorContactDetails')
      .subscribe(({ authorContactProfile }) => {
        this.authorProfile = null;
        if (authorContactProfile) {
          this.authorProfile = authorContactProfile;
        }
      });

    this.store
      .select('outletContactDetails')
      .subscribe(({ outletContactProfile }) => {
        this.outletProfile = null;
        if (outletContactProfile) {
          this.outletProfile = outletContactProfile;
        }
      });

    this.authorSubjectKeyUp
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((name) => {
        this.getAuthors(name);
      });

    this.outletSubjectKeyUp
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((name) => {
        this.getOutlets(name);
      });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
      this.initializeIgnoredLabelDefaults();
    });
  }

  getOutletGroups(): Promise<void> {
    this.outletGroup = [];
    this.isLoadingGroups = true;
    const isAdmin = this.isAdmin();
    return new Promise((resolve, reject) => {
      this.outletService.getOutletGroups().subscribe(
        (groups: OutletGroups[]) => {
          this.store.dispatch(setOutletGroups({ outletGroups: groups }));
          const filteredGroups = selectableOutletGroups(groups, isAdmin);
          this.outletGroup = filteredGroups.map((group) => ({
            label: group.name,
            value: group.id,
          }));
          this.outletGroupOptionsChange.emit([...this.outletGroup]);
          this.isLoadingGroups = false;
          resolve();
        },
        (error) => {
          console.error('Error loading outlet groups:', error);
          this.isLoadingGroups = false;
          reject(error);
        }
      );
    });
  }

  onResetFilter() {
    this.resetFilterFields();
    this.store.dispatch(resetAdvancedSearchFilters({ onResetFilters: false }));
  }

  private resetFilterFields() {
    this.timeframeSelected = 'ONE_YEAR';
    this.matchTypeSelected = 'STANDARD';
    this.authorInfluSelected = false;
    this.outletInfluSelected = false;
    this.authorFollowSelected = null;
    this.outletFollowSelected = null;
    this.analystSelected = null;
    this.locationSelected = null;
    this.outletGroupSelected = [];
    this.toDate = null;
    this.fromDate = null;
    this.selectedAuthorsInclude = null;
    this.selectedAuthorsExclude = null;
    this.selectedOutletsExclude = null;
    this.selectedOutletsInclude = null;
    this.authorLabels = [];
    const ignoreLabelIds = this.searchFilterDefaults.getIgnoreLabelIds();
    this.excludeAuthorLabels = [...ignoreLabelIds];
    this.outletLabels = [];
    this.excludeOutletLabels = [...ignoreLabelIds];
    this.ignoreDefaultsInitialized = ignoreLabelIds.length > 0;
  }

  private hydrateSearchFilters(search: Search) {
    this.resetFilterFields();
    if (!search) { return; }

    this.timeframeSelected = search.timeframe || 'ONE_YEAR';
    this.matchTypeSelected = search.matchType || 'STANDARD';
    this.authorInfluSelected = search.topAuthorsOnly || false;
    this.outletInfluSelected = search.topOutletsOnly || false;
    this.authorFollowSelected = search.authorFollowSelected || null;
    this.outletFollowSelected = search.outletFollowSelected || null;
    this.analystSelected = search.analysts || null;
    this.locationSelected = search.countries || null;
    this.outletGroupSelected = search.outletGroups?.length ? [...search.outletGroups] : [];
    this.fromDate = this.coerceDate(search.from);
    this.toDate = this.coerceDate(search.to);
    this.authorLabels = [...(search.authorLabels || [])];
    this.excludeAuthorLabels = [...(search.excludeAuthorLabels || [])];
    this.outletLabels = [...(search.outletLabels || [])];
    this.excludeOutletLabels = [...(search.excludeOutletLabels || [])];
    this.ignoreDefaultsInitialized = true;

    (search.authors || []).forEach((id) => this.getAuthorById(id, 'include'));
    (search.excludeAuthors || []).forEach((id) => this.getAuthorById(id, 'exclude'));
    (search.outlets || []).forEach((id) => this.getOutletById(id, 'include'));
    (search.excludeOutlets || []).forEach((id) => this.getOutletById(id, 'exclude'));
  }

  public syncSearchFilters(search: Search | null) {
    this.hydrateSearchFilters(search);
    this.snapshotFilters();
  }

  private initializeIgnoredLabelDefaults() {
    if (this.ignoreDefaultsInitialized) { return; }
    const ignoreLabelIds = this.searchFilterDefaults.getIgnoreLabelIds();
    if (!ignoreLabelIds.length) { return; }

    this.excludeAuthorLabels = [...ignoreLabelIds];
    this.excludeOutletLabels = [...ignoreLabelIds];
    this.ignoreDefaultsInitialized = true;
  }

  private coerceDate(value: Date | string): Date {
    if (!value) { return null; }
    return value instanceof Date ? value : new Date(value);
  }

  getAuthors(name) {
    const filtered: any[] = [];
    this.authorService.searchAuthorsByName(name).subscribe(
      (response: any) => {
        filtered.push(response);
        this.filteredAuthors = filtered[0];
      },
      ({ error }) => {
        console.log(error);
      }
    );
  }

  getOutlets(name) {
    const filtered: any[] = [];
    this.outletService.searchOutletsByName(name).subscribe(
      (response: any) => {
        filtered.push(response);
        this.filteredOutlets = filtered[0];
      },
      ({ error }) => {
        console.log(error);
      }
    );
  }

  getOutletById(id, target: 'include' | 'exclude' = 'include') {
    this.outletService.getOutletById(id).subscribe((response) => {
      const selectedOutlets = target === 'exclude' ? this.selectedOutletsExclude : this.selectedOutletsInclude;
      const nextSelectedOutlets = selectedOutlets || [];
      if (!nextSelectedOutlets.some((o) => o.id === response.id)) {
        if (target === 'exclude') {
          this.selectedOutletsExclude = [
            ...nextSelectedOutlets,
            response,
          ];
        } else {
          this.selectedOutletsInclude = [
            ...nextSelectedOutlets,
            response,
          ];
        }
      }
    });
  }

  getAuthorById(id, target: 'include' | 'exclude' = 'include') {
    this.authorService.getAuthorById(id).subscribe((response) => {
      const selectedAuthors = target === 'exclude' ? this.selectedAuthorsExclude : this.selectedAuthorsInclude;
      const nextSelectedAuthors = selectedAuthors || [];
      if (!nextSelectedAuthors.some((a) => a.id === response.id)) {
        if (target === 'exclude') {
          this.selectedAuthorsExclude = [
            ...nextSelectedAuthors,
            response,
          ];
        } else {
          this.selectedAuthorsInclude = [
            ...nextSelectedAuthors,
            response,
          ];
        }
      }
    });
  }

  private buildSearch(): Search {
    return new Search({
      keywords: this.keywords,
      timeframe: this.timeframeSelected,
      matchType: this.matchTypeSelected,
      analysts: this.analystSelected,
      countries: this.locationSelected,
      outletGroups: (this.outletGroupSelected || []).filter((id) => id !== null),
      topOutletsOnly: this.outletInfluSelected,
      topAuthorsOnly: this.authorInfluSelected,
      authorFollowSelected: this.authorFollowSelected,
      outletFollowSelected: this.outletFollowSelected,
      from: eq(this.timeframeSelected, 'CUSTOM') ? this.fromDate : null,
      to: eq(this.timeframeSelected, 'CUSTOM') ? this.toDate : null,
      authors: map('id', this.selectedAuthorsInclude),
      excludeAuthors: map('id', this.selectedAuthorsExclude),
      excludeOutlets: map('id', this.selectedOutletsExclude),
      outlets: map('id', this.selectedOutletsInclude),
      authorLabels: this.authorLabels || [],
      excludeAuthorLabels: this.excludeAuthorLabels || [],
      outletLabels: this.outletLabels || [],
      excludeOutletLabels: this.excludeOutletLabels || [],
    });
  }

  public runAdvanceSearch() {
    // While the filters dialog is open, changes are staged only. The search is
    // emitted once when the user clicks Apply (committingFilters === true).
    if (this.showFilters && !this.committingFilters) {
      return;
    }
    const search = this.buildSearch();

    switch (this.filterType) {
      case FilterType.NEWS_FEED:
        this.newsFeedFilters.emit(search);
        break;
      case FilterType.COVERAGE_REPORT:
        this.coverageFilters.emit(search);
        break;
      default:
        this.advanceSearchOptions.emit(search);
    }
  }

  public handleAdvanceSearch() {
    this.runAdvanceSearch();
    this.onClose();
  }

  private snapshotFilters() {
    this.filtersSnapshot = {
      timeframeSelected: this.timeframeSelected,
      matchTypeSelected: this.matchTypeSelected,
      authorInfluSelected: this.authorInfluSelected,
      outletInfluSelected: this.outletInfluSelected,
      authorFollowSelected: this.authorFollowSelected,
      outletFollowSelected: this.outletFollowSelected,
      analystSelected: this.analystSelected,
      locationSelected: this.locationSelected,
      outletGroupSelected: this.outletGroupSelected ? [...this.outletGroupSelected] : this.outletGroupSelected,
      fromDate: this.fromDate,
      toDate: this.toDate,
      selectedAuthorsInclude: this.selectedAuthorsInclude ? [...this.selectedAuthorsInclude] : this.selectedAuthorsInclude,
      selectedAuthorsExclude: this.selectedAuthorsExclude ? [...this.selectedAuthorsExclude] : this.selectedAuthorsExclude,
      selectedOutletsInclude: this.selectedOutletsInclude ? [...this.selectedOutletsInclude] : this.selectedOutletsInclude,
      selectedOutletsExclude: this.selectedOutletsExclude ? [...this.selectedOutletsExclude] : this.selectedOutletsExclude,
      authorLabels: [...(this.authorLabels || [])],
      excludeAuthorLabels: [...(this.excludeAuthorLabels || [])],
      outletLabels: [...(this.outletLabels || [])],
      excludeOutletLabels: [...(this.excludeOutletLabels || [])],
    };
  }

  public onApplyFilters() {
    // Commit the staged selection: emit the search once, then close.
    this.committingFilters = true;
    this.runAdvanceSearch();
    this.committingFilters = false;
    this.onClose();
  }

  public applyEmbeddedFilters() {
    if (!this.embedded) { return; }
    this.committingFilters = true;
    this.runAdvanceSearch();
    this.committingFilters = false;
  }

  public onCancelFilters() {
    // Discard the staged changes by restoring the snapshot taken on open.
    // Nothing was emitted while the dialog was open, so no re-run is needed.
    if (this.filtersSnapshot) {
      Object.assign(this, this.filtersSnapshot);
    }
    this.onClose();
  }

  public onClose() {
    this.toggle.emit(false);
  }

  public findAuthorsByName(event) {
    this.authorSubjectKeyUp.next(event.query);
  }

  public findOutletsByName(event) {
    this.outletSubjectKeyUp.next(event.query);
  }

  public onSelectNewFilter(event) {
    this.runAdvanceSearch();
  }

  public onUnselectAuthor(event) {
    if (this.authorProfile) {
      if (eq(this.authorProfile.id, event.id)) {
        this.selectedAuthorsInclude = this.selectedAuthorsInclude.filter(
          (item) => item.id !== event.id
        );
        this.store.dispatch(unSetInputSearchPlaceholder());
        this.store.dispatch(resetAuthorProfile());
      }
    }
    this.runAdvanceSearch();
  }

  public onUnselectOutlet(event) {
    if (this.outletProfile) {
      if (eq(this.outletProfile.id, event.id)) {
        this.selectedOutletsInclude = this.selectedOutletsInclude.filter(
          (item) => item.id !== event.id
        );
        this.store.dispatch(unSetInputSearchPlaceholder());
        this.store.dispatch(resetOutletProfile());
      }
    }
    this.runAdvanceSearch();
  }

  public onTimeframeChange({ value }) {
    if (!eq(value, 'CUSTOM')) {
      setTimeout(() => {
        this.runAdvanceSearch();
      }, 1000);
    }
  }

  public applyDateRage() {
    if (this.fromDate && this.toDate) {
      this.runAdvanceSearch();
    }
  }

  isAdmin() {
    return this.authenticationService.isAdminUser();
  }

  hasReportingAccess() {
    return this.authenticationService.hasReportingAccess();
  }
}
